import { supabase } from "@/integrations/supabase/client";

// Export the Message type definition with sender/receiver IDs
export interface Message {
  id: number;
  senderId: string;
  receiverId: string;
  text: string;
  timestamp: Date;
}

// Export the Recipient/User type definition
export interface Recipient {
  id: string;
  name: string;
  status: "online" | "offline" | "away";
  avatarInitials: string;
  avatarUrl?: string;
}

// Export the Conversation type definition
export interface Conversation {
  convoId: string;
  otherUserId: string;
  otherUserName: string;
  otherUserInitials: string;
  lastMessagePreview: string;
  lastMessageTime: Date;
  unreadCount?: number;
  serviceId?: string;
  serviceTitle?: string;
}

// Get the current user ID from Supabase session
const getCurrentUserId = async (): Promise<string> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error("No authenticated user found");
  }
  return session.user.id;
};

// Function to get conversations for a user
export const getConversations = async (): Promise<Conversation[]> => {
  try {
    const userId = await getCurrentUserId();
    console.log(`Fetching conversations for user: ${userId}`);
    
    // Get conversations from Supabase with service_id included
    const { data: conversationsData, error } = await supabase
      .from('conversations')
      .select(`
        id,
        participant1_id,
        participant2_id,
        last_message,
        updated_at,
        service_id
      `)
      .or(`participant1_id.eq.${userId},participant2_id.eq.${userId}`)
      .order('updated_at', { ascending: false });
    
    if (error) {
      console.error("Error fetching conversations:", error);
      return [];
    }

    if (!conversationsData || conversationsData.length === 0) {
      return [];
    }

    // Process the data to match the Conversation type
    const conversations: Conversation[] = await Promise.all(
      conversationsData.map(async (convo) => {
        try {
          // Safe type assertion for Supabase data
          const typedConvo = convo as {
            id: string;
            participant1_id: string;
            participant2_id: string;
            last_message: string | null;
            updated_at: string;
            service_id: string | null;
          };
          
          // Determine the other participant
          const otherUserId = typedConvo.participant1_id === userId 
            ? typedConvo.participant2_id 
            : typedConvo.participant1_id;
          
          // Get the other user's profile data including avatar
          const { data: profileData } = await supabase
            .from('profiles')
            .select('first_name, last_name, avatar_url')
            .eq('id', otherUserId)
            .single();
          
          const otherUserName = profileData ? 
            `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim() || 'Unknown User' : 
            'Unknown User';
            
          // Get initials from name
          const initials = otherUserName
            .split(' ')
            .map(name => name[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
          
          const otherUserAvatarUrl = profileData?.avatar_url || undefined;

          // Get unread count
          const { count, error: countError } = await supabase
            .from('messages')
            .select('*', { count: 'exact', head: true })
            .eq('conversation_id', typedConvo.id)
            .eq('is_read', false)
            .neq('sender_id', userId);
            
          const unreadCount = countError ? 0 : count || 0;
          
          // If this conversation is about a service, get the service details
          let serviceTitle = undefined;
          let serviceId = undefined;
          
          if (typedConvo.service_id) {
            serviceId = typedConvo.service_id;
            
            const { data: serviceData } = await supabase
              .from('services')
              .select('title')
              .eq('id', typedConvo.service_id)
              .single();
              
            if (serviceData) {
              serviceTitle = serviceData.title;
            }
          }

          const lastMessageText = typedConvo.last_message || 'No messages yet';
          // Add service title to the message preview if it's a service conversation
          const lastMessagePreview = serviceTitle && !lastMessageText.includes(serviceTitle) 
            ? `[${serviceTitle}] ${lastMessageText}` 
            : lastMessageText;

          return {
            convoId: typedConvo.id,
            otherUserId: otherUserId,
            otherUserName: otherUserName,
            otherUserInitials: initials,
            otherUserAvatarUrl: otherUserAvatarUrl,
            lastMessagePreview: lastMessagePreview,
            lastMessageTime: new Date(typedConvo.updated_at),
            unreadCount: unreadCount > 0 ? unreadCount : undefined,
            serviceId: serviceId,
            serviceTitle: serviceTitle
          };
        } catch (error) {
          console.error("Error processing conversation:", error);
          return null;
        }
      })
    );
    
    // Filter out any failed conversations
    return conversations.filter(Boolean) as Conversation[];
    
  } catch (error) {
    console.error("Error fetching conversations:", error);
    return [];
  }
};

// Define the return type for getMessages
interface MessagesResponse {
  messages: Message[];
  recipient: Recipient;
}

// Function to get messages for a specific conversation
export const getMessages = async (conversationId?: string): Promise<MessagesResponse> => {
  try {
    console.log(`Fetching messages for conversation: ${conversationId}`);
    
    if (!conversationId) {
      throw new Error("Conversation ID is required");
    }

    // Get the current user ID
    const userId = await getCurrentUserId();
    
    // Get conversation details to determine the recipient
    const { data: conversation, error: convoError } = await supabase
      .from('conversations')
      .select('participant1_id, participant2_id')
      .eq('id', conversationId)
      .single();
    
    if (convoError || !conversation) {
      console.error("Error fetching conversation:", convoError);
      throw new Error("Conversation not found");
    }
    
    // Determine the recipient ID
    const recipientId = conversation.participant1_id === userId 
      ? conversation.participant2_id 
      : conversation.participant1_id;
    
    // Get recipient profile
    const { data: recipientProfile, error: profileError } = await supabase
      .from('profiles')
      .select('first_name, last_name, avatar_url')
      .eq('id', recipientId)
      .single();
    
    if (profileError) {
      console.error("Error fetching recipient profile:", profileError);
      throw new Error("Recipient not found");
    }
    
    // Create recipient object
    const recipient: Recipient = {
      id: recipientId,
      name: recipientProfile ? 
        `${recipientProfile.first_name || ''} ${recipientProfile.last_name || ''}`.trim() || "Unknown User" : 
        "Unknown User",
      status: "online", // This would be dynamic in a real application
      avatarInitials: (recipientProfile ? 
        `${recipientProfile.first_name || ''} ${recipientProfile.last_name || ''}`.trim() || "U" : 
        "U")
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase()
        .substring(0, 2),
      avatarUrl: recipientProfile?.avatar_url || undefined
    };
    
    // Get all messages for this conversation
    const { data: messagesData, error: messagesError } = await supabase
      .from('messages')
      .select('*')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: true });
    
    if (messagesError) {
      console.error("Error fetching messages:", messagesError);
      throw new Error("Failed to load messages");
    }
    
    // Mark messages from recipient as read
    await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('conversation_id', conversationId)
      .eq('is_read', false)
      .neq('sender_id', userId);
    
    // Transform messages to match our Message type
    const messages: Message[] = messagesData.map(msg => ({
      id: Number(msg.id.replace(/-/g, '')), // Convert UUID to number for compatibility
      senderId: msg.sender_id,
      receiverId: msg.sender_id === userId ? recipientId : userId,
      text: msg.text,
      timestamp: new Date(msg.created_at)
    }));
    
    return { messages, recipient };
    
  } catch (error) {
    console.error("Error in getMessages:", error);
    // Return empty data in case of error
    return {
      messages: [],
      recipient: {
        id: "",
        name: "Unknown",
        status: "offline",
        avatarInitials: "UN"
      }
    };
  }
};

// Function to send a new message
export const sendMessage = async (message: Omit<Message, 'id' | 'timestamp'>): Promise<Message> => {
  try {
    console.log(`Sending message: ${JSON.stringify(message)}`);
    
    // Check if conversation exists
    const userId = await getCurrentUserId();
    let conversationId: string;
    
    if (message.receiverId) {
      // Check if conversation exists between these users
      const { data: existingConvo } = await supabase
        .from('conversations')
        .select('id')
        .or(`and(participant1_id.eq.${userId},participant2_id.eq.${message.receiverId}),and(participant1_id.eq.${message.receiverId},participant2_id.eq.${userId})`)
        .maybeSingle();
      
      if (existingConvo) {
        conversationId = existingConvo.id;
      } else {
        // Create a new conversation
        const { data: newConvo, error: convoError } = await supabase
          .from('conversations')
          .insert({
            participant1_id: userId,
            participant2_id: message.receiverId
          })
          .select('id')
          .single();
        
        if (convoError || !newConvo) {
          throw new Error("Failed to create conversation");
        }
        
        conversationId = newConvo.id;
      }
    } else {
      throw new Error("Recipient ID is required");
    }
    
    // Insert the message
    const { data: newMessage, error: msgError } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: userId,
        text: message.text
      })
      .select('*, conversations!inner(*)')
      .single();
    
    if (msgError || !newMessage) {
      throw new Error("Failed to send message");
    }
    
    // Update the conversation's last_message and updated_at
    await supabase
      .from('conversations')
      .update({
        last_message: message.text,
        updated_at: new Date().toISOString()
      })
      .eq('id', conversationId);
    
    // Return the message in our format
    return {
      id: Number(newMessage.id.replace(/-/g, '')), // Convert UUID to number for compatibility
      senderId: newMessage.sender_id,
      receiverId: message.receiverId,
      text: newMessage.text,
      timestamp: new Date(newMessage.created_at)
    };
    
  } catch (error) {
    console.error("Error sending message:", error);
    throw error;
  }
};

// Setup real-time subscription to messages for a conversation
export const subscribeToConversation = (
  conversationId: string,
  onNewMessage: (message: Message) => void
) => {
  const channel = supabase
    .channel(`messages-${conversationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      async (payload) => {
        try {
          // Get user details to complete message object
          const userId = await getCurrentUserId();
          const receiverId = payload.new.sender_id === userId ? 
            payload.new.receiver_id || '' : // This might not be in the payload
            userId;

          const message: Message = {
            id: Number(payload.new.id.replace(/-/g, '')),
            senderId: payload.new.sender_id,
            receiverId: receiverId,
            text: payload.new.text,
            timestamp: new Date(payload.new.created_at)
          };
          
          onNewMessage(message);
        } catch (error) {
          console.error('Error processing real-time message:', error);
        }
      }
    )
    .subscribe();

  // Return unsubscribe function
  return () => {
    supabase.removeChannel(channel);
  };
};

// Function to get recipient details
export const getRecipient = async (recipientId: string): Promise<Recipient | null> => {
  try {
    console.log(`Fetching recipient details: ${recipientId}`);
    
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('id', recipientId)
      .single();
    
    if (error || !profile) {
      console.error("Error fetching recipient:", error);
      return null;
    }
    
    const name = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || "Unknown User";
                 
    return {
      id: recipientId,
      name: name,
      status: "online", // This would be dynamic in a real application
      avatarInitials: name
        .split(' ')
        .map(name => name[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    };
    
  } catch (error) {
    console.error("Error getting recipient:", error);
    return null;
  }
};

// Mark messages as read
export const markMessagesAsRead = async (conversationId: string): Promise<void> => {
  try {
    const userId = await getCurrentUserId();
    
    await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('conversation_id', conversationId)
      .eq('is_read', false)
      .neq('sender_id', userId);
      
  } catch (error) {
    console.error("Error marking messages as read:", error);
    throw error;
  }
};

// Get unread message count for a user
export const getUnreadMessageCount = async (): Promise<number> => {
  try {
    const userId = await getCurrentUserId();
    
    // Get all conversations for this user
    const { data: conversationsData, error: convoError } = await supabase
      .from('conversations')
      .select('id')
      .or(`participant1_id.eq.${userId},participant2_id.eq.${userId}`);
    
    if (convoError || !conversationsData) {
      return 0;
    }
    
    // Get all unread messages across all conversations
    const conversationIds = conversationsData.map(c => c.id);
    
    if (conversationIds.length === 0) {
      return 0;
    }
    
    const { count, error: countError } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .in('conversation_id', conversationIds)
      .eq('is_read', false)
      .neq('sender_id', userId);
      
    return countError ? 0 : count || 0;
    
  } catch (error) {
    console.error("Error getting unread message count:", error);
    return 0;
  }
};

// Function to simulate a response from the recipient - we'll replace this with real-time functionality later
export const simulateResponse = async (recipientId: string, originalMessage: Message): Promise<Message> => {
  try {
    console.log(`Simulating response from recipient: ${recipientId}`);
    
    // Create a realistic delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Create and send the response message
    const responseText = "Thanks for your message! I'll get back to you soon.";
    const userId = await getCurrentUserId();
    
    const { data: newMessage, error: msgError } = await supabase
      .from('messages')
      .insert({
        conversation_id: originalMessage.id.toString(), // Assuming originalMessage.id is the conversation ID
        sender_id: recipientId,
        text: responseText
      })
      .select('*')
      .single();
    
    if (msgError || !newMessage) {
      throw new Error("Failed to simulate response");
    }
    
    // Update the conversation's last_message and updated_at
    await supabase
      .from('conversations')
      .update({
        last_message: responseText,
        updated_at: new Date().toISOString()
      })
      .eq('id', originalMessage.id.toString()); // Assuming originalMessage.id is the conversation ID
    
    // Return the message in our format
    return {
      id: Number(newMessage.id.replace(/-/g, '')), // Convert UUID to number for compatibility
      senderId: recipientId,
      receiverId: userId,
      text: responseText,
      timestamp: new Date(newMessage.created_at)
    };
    
  } catch (error) {
    console.error("Error simulating response:", error);
    throw error;
  }
};

// Function to simulate an incoming message - we'll replace this with real-time functionality later
export const simulateIncomingMessage = async (recipientId: string): Promise<Message> => {
  try {
    const userId = await getCurrentUserId();
    
    // Sample responses
    const sampleResponses = [
      "I'll check my calendar and get back to you.",
      "That sounds great! When would be a good time?",
      "Thanks for the information. I'll review it.",
      "Could you please provide more details?",
      "I'm available next week if that works for you.",
      "Let me think about it and respond later.",
      "I have a few questions before we proceed.",
      "Perfect! Looking forward to our meeting.",
    ];
    
    // Get a random response
    const randomIndex = Math.floor(Math.random() * sampleResponses.length);
    const randomMessage = sampleResponses[randomIndex];
    
    // Find or create conversation
    const { data: existingConvo } = await supabase
      .from('conversations')
      .select('id')
      .or(`and(participant1_id.eq.${userId},participant2_id.eq.${recipientId}),and(participant1_id.eq.${recipientId},participant2_id.eq.${userId})`)
      .maybeSingle();
    
    let conversationId: string;
    
    if (existingConvo) {
      conversationId = existingConvo.id;
    } else {
      // Create a new conversation
      const { data: newConvo, error: convoError } = await supabase
        .from('conversations')
        .insert({
          participant1_id: recipientId,
          participant2_id: userId
        })
        .select('id')
        .single();
      
      if (convoError || !newConvo) {
        throw new Error("Failed to create conversation");
      }
      
      conversationId = newConvo.id;
    }
    
    // Create the message
    const { data: newMessage, error: msgError } = await supabase
      .from('messages')
      .insert({
        conversation_id: conversationId,
        sender_id: recipientId,
        text: randomMessage,
        is_read: false
      })
      .select('*')
      .single();
    
    if (msgError || !newMessage) {
      throw new Error("Failed to create simulated message");
    }
    
    // Update the conversation
    await supabase
      .from('conversations')
      .update({
        last_message: randomMessage,
        updated_at: new Date().toISOString()
      })
      .eq('id', conversationId);
    
    // Return the message
    return {
      id: Number(newMessage.id.replace(/-/g, '')),
      senderId: recipientId,
      receiverId: userId,
      text: randomMessage,
      timestamp: new Date(newMessage.created_at)
    };
    
  } catch (error) {
    console.error("Error simulating incoming message:", error);
    throw new Error("Failed to simulate incoming message");
  }
};

// Function to update a service request status
export const updateRequestStatus = async (conversationId: string, status: 'accepted' | 'rejected'): Promise<void> => {
  try {
    const userId = await getCurrentUserId();
    
    // First, check if the user is allowed to update the status
    // (i.e., they are the service provider)
    const { data: conversation, error: convoError } = await supabase
      .from('conversations')
      .select('service_id')
      .eq('id', conversationId)
      .single();
      
    if (convoError) {
      console.error('Error fetching conversation:', convoError);
      throw new Error('Could not verify permission to update request status');
    }
    
    if (!conversation.service_id) {
      throw new Error('This is not a service request conversation');
    }
    
    // Check if current user is the service provider
    const { data: service, error: serviceError } = await supabase
      .from('services')
      .select('user_id')
      .eq('id', conversation.service_id)
      .single();
      
    if (serviceError) {
      console.error('Error fetching service:', serviceError);
      throw new Error('Could not verify service provider');
    }
    
    if (service.user_id !== userId) {
      throw new Error('Only the service provider can update the request status');
    }
    
    // Update the request status
    const { error: updateError } = await supabase
      .from('conversations')
      .update({ request_status: status })
      .eq('id', conversationId);
      
    if (updateError) {
      console.error('Error updating request status:', updateError);
      throw new Error('Failed to update request status');
    }
    
  } catch (error) {
    console.error('Error updating request status:', error);
    throw error;
  }
};

// Add this function to the exports:
export const markServiceAsComplete = async (conversationId: string) => {
  if (!conversationId) {
    throw new Error("Conversation ID is required");
  }

  const { data, error } = await supabase
    .from('conversations')
    .update({ service_completed: true })
    .eq('id', conversationId);

  if (error) {
    console.error("Error marking service as complete:", error);
    throw error;
  }

  return data;
};
